import { tt, type WalletScope } from '@agos/shared'

/**
 * *Prehľady* without the day's total - `FRONTEND.md` §9.729, owner O99, FB-1030.
 *
 * **The owner**: *"oni chcú aj to aby vedeli nastaviť prehľady kto ma vidieť celkovú tržbu"*. The total became the
 * right `summaries_revenue` (BACKEND.md §10A.377), apart from `summaries`, because since O47 (§9.661) every employee
 * opens this page for their own peňaženka - and the page's key handed them the restaurant's takings with it.
 *
 * **What the right covers on this page, and why the wallets are in it.** The day summary is the total, the average,
 * the payment and channel split and the storno count - all of it withheld. **The overview of every wallet is
 * withheld too**: the colleagues' wallets add up to the attributed takings, so an administrator shown all of them
 * without the right would read the total off the column sums. Somebody without the right is shown what an employee
 * without the overview is shown - their own wallet where their role keeps one (§9.661), and nothing otherwise.
 */

/** The right the day's total asks for - BACKEND.md §10A.377. */
export const SUMMARIES_REVENUE = 'summaries_revenue'

/**
 * Whose wallets this page shows, once the total is a right of its own.
 *
 * `scope` is `walletsScope`'s answer, read off the overview endpoint's own guard; this narrows it and never widens
 * it. **`ownKind` is required** rather than looked up here, because the page already holds it (`ownWalletKind`) and a
 * second derivation is how the two would come to disagree.
 */
export function walletsScopeWithTheTotal(
  scope: WalletScope,
  maySeeTotal: boolean,
  ownKind: 'waiter' | 'driver' | null,
): WalletScope {
  if (maySeeTotal || scope !== 'everyone') return scope
  return ownKind === null ? 'none' : 'self'
}

/**
 * What stands where the total was - one sentence, naming the right.
 *
 * Names the **right**, as the board's *Vyraziť* does (§9.728): somebody who should see the total needs a tick in
 * *Oprávnenia*, and the right's own label is the word they will find there.
 */
export function totalWithheldNote(): string {
  return `${tt('Celkovú tržbu dňa vám tento účet nezobrazuje - zobrazí ju právo')} „${tt('Prehľady - celková tržba dňa')}".`
}

/**
 * The right that shows everyone's wallets - BACKEND.md §10A.449, `FRONTEND.md` §9.803, owner O150.
 *
 * **The owner**: *"make sure that «Sumáre» can have some permission for showing sumáre from everyone"*. The page chose
 * by role (`walletsScope`, §9.661), so no tick could show a shift lead the colleagues' wallets.
 */
export const SUMMARIES_WALLETS = 'summaries_wallets'

/**
 * Whose wallets, once the right is asked: everyone's for whoever holds it, else what the role answers.
 *
 * **Widens only.** Then `walletsScopeWithTheTotal` narrows exactly as before, so somebody without the total is still
 * kept to their own (their column sums are the takings, §9.729) - and an administrator is not changed either way.
 */
export function walletsScopeWithTheRight(scope: WalletScope, holdsWalletsRight: boolean): WalletScope {
  return holdsWalletsRight ? 'everyone' : scope
}

/**
 * The line above the own wallet, naming the right that is missing - §9.728's rule, as `totalWithheldNote` does.
 *
 * It said *Peňaženky kolegov vidí administrátor*, which stopped being the whole truth with §9.803. Without the
 * wallets right it names that right; with it, the only thing still missing is the total.
 */
export function colleaguesWalletsNote(holdsWalletsRight: boolean): string {
  const missing = holdsWalletsRight
    ? `${tt('Peňaženky kolegov sa zobrazia spolu s právom')} „${tt('Prehľady - celková tržba dňa')}".`
    : `${tt('Peňaženky kolegov zobrazí právo')} „${tt('Sumáre - peňaženky všetkých zamestnancov')}".`
  return `${missing} ${tt('Tu je vaša vlastná.')}`
}
